import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { Pencil, Trash2 } from "lucide-react";
import {
  fetchDesignations,
  addDesignation,
  updateDesignation,
  deleteDesignation,
  updateDesignationStatus,
} from "../../redux/designationSlice";

const DesignationDetail = () => {
  const { register, handleSubmit, reset, setValue } = useForm();
  const dispatch = useDispatch();
  const { designations, status, error } = useSelector((state) => state.designations);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Fetch designations on component mount
  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchDesignations());
    }
  }, [status, dispatch]);

  // Add or Update Designation
  const onSubmit = async (data) => {
    const designationData = { name: data.departmentName }; // Map departmentName to name
    try {
      if (isEditing) {
        await dispatch(updateDesignation({ id: editId, updatedData: designationData })).unwrap();
        setIsEditing(false);
        setEditId(null);
      } else {
        await dispatch(addDesignation(designationData)).unwrap();
      }
      reset();
    } catch (err) {
      console.error("Error submitting designation:", err);
    }
  };

  // Delete Designation
  const handleDelete = async (id) => {
    try {
      await dispatch(deleteDesignation(id)).unwrap();
    } catch (err) {
      console.error("Error deleting designation:", err);
    }
  };

  // Edit Designation
  const handleEdit = (id) => {
    const desig = designations.find((desig) => desig.id === id);
    if (desig) {
      setValue("departmentName", desig.name);
      setIsEditing(true);
      setEditId(id);
    }
  };

  // Activate/Disable Designation
  const handleActivate = async (id) => {
    const desig = designations.find((desig) => desig.id === id);
    if (desig) {
      const newStatus = desig.status === "Active" ? "Disabled" : "Active";
      try {
        await dispatch(updateDesignationStatus({ id, status: newStatus })).unwrap();
      } catch (err) {
        console.error("Error updating designation status:", err);
      }
    }
  };

  // Render loading or error states
  if (status === "loading") {
    return <div className="p-6">Loading designations...</div>;
  }

  if (status === "failed") {
    return <div className="p-6 text-red-700">Error: {error}</div>;
  }

  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Add / Edit Designation Form */}
      <div className="bg-white shadow-md rounded-lg p-4 mb-6 h-60">
        <h2 className="text-lg font-semibold mb-4">
          {isEditing ? "Edit Designation" : "Add New Designation"}
        </h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 text-sm">
          {/* Fields Wrapper */}
          <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
            {/* Department Name Field */}
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                Designation Name *
              </label>
              <input
                type="text"
                {...register("departmentName", { required: true })}
                className="w-full border border-gray-300 rounded-md p-2"
                placeholder="Must be Unique"
              />
            </div>
          </div>
          {/* Buttons */}
          <div className="flex space-x-4">
            <button
              type="submit"
              className="bg-[#241763] text-white px-4 py-2 rounded-md"
            >
              {isEditing ? "Update" : "Submit"}
            </button>
            <button
              type="button"
              onClick={() => {
                reset();
                setIsEditing(false);
                setEditId(null);
              }}
              className="bg-[#241763]/10 text-black px-4 py-2 rounded-md"
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* Designations Table */}
      <div className="bg-white shadow-md rounded-lg p-4 overflow-x-auto text-sm">
        <div className="flex justify-between items-center mb-4">
          <input
            type="text"
            placeholder="Search By Designation name"
            className="border border-gray-300 rounded-md p-2 w-1/2"
          />
          <button className="bg-gray-300 text-black px-4 py-2 rounded-md hover:bg-gray-400">
            Clear
          </button>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="py-2">Designation NAME</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {designations.map((desig) => (
              <tr key={desig.id} className="hover:bg-gray-50">
                <td>{desig.name}</td>
                <td>
                  <span
                    className={`px-2 py-1 rounded-md text-sm ${
                      desig.status === "Active"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {desig.status}
                  </span>
                </td>
                <td className="flex space-x-2 py-3">
                  <button
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded mr-2 hover:bg-[#241763]/60 group"
                    onClick={() => handleEdit(desig.id)}
                  >
                    <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    onClick={() => handleDelete(desig.id)}
                  >
                    <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    onClick={() => handleActivate(desig.id)}
                    className="bg-green-500 text-white px-3 py-1 rounded-md hover:bg-green-600"
                  >
                    {desig.status === "Active" ? "Disable" : "Activate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DesignationDetail;